import { Dropdown, type MenuProps } from 'antd';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileMenuActionEnum } from '../../../enums/FileMenuActionEnum';
import { SpecialFolderTypeEnum } from '../../../enums/SpecialFolderTypeEnum';
import type File from '../../../interfaces/File';
import type FileAction from '../../../interfaces/FileAction';
import { type FileActionKey } from '../../../lib/fileActions';
import {
  FILE_PREVIEW_EXTENSIONS_IMAGES,
  getFileTitle,
  getFolderHex,
  removeShortcutExtension,
} from '../../../utils/utils';
import FileIcon from '../../Common/FileIcon';
import ShortcutHint from '../../Common/ShortcutHint';
import TruncatedTooltip from '../../Common/TruncatedTooltip';
import { Icon } from '../../Core/Icon';

// Props for the FileContextMenu component
interface FileContextMenuProps {
  files: File[];
  position: { x: number; y: number; direction: 'up' | 'down' };
  visible: boolean;
  isUploadDisabledInCurrentFolder: boolean;
  onClose: () => void;
  onAction: (filesIds: number[], action: FileActionKey) => void;
  filesActions: FileAction[];
}

// FileContextMenu is a React component that renders a context menu for files.
// It supports multiple files and custom actions.
export const FileContextMenu: React.FC<FileContextMenuProps> = ({
  files,
  position,
  visible,
  onClose,
  onAction,
  filesActions,
}) => {
  // Localization hook
  const { t } = useTranslation();

  /**
   * Returns the first file in the list,
   * used for displaying an icon and title in the menu header.
   */
  const firstFile = useMemo(() => files?.[0], [files]);

  /**
   * Maps FileAction[] to Ant Design MenuProps['items'] structure,
   * handling translation, icons, disabled states, submenu rendering, and custom gradients.
   * Memoized for performance.
   */
  const menuItems: MenuProps['items'] = useMemo(() => {
    // Gets label and translates if needed
    const getLabel = (label: FileAction['label']) => (typeof label === 'string' ? t(label) : label);

    /**
     * Recursively maps FileAction to Antd menu item.
     */
    const mapActionToMenuItem = (action: FileAction): NonNullable<MenuProps['items']>[number] => {
      if (action.type === 'divider') {
        return { type: 'divider' };
      }

      const isAiAction = [
        FileMenuActionEnum.ArtificialIntelligence,
        FileMenuActionEnum.ArtificialIntelligenceSummary,
        FileMenuActionEnum.ArtificialIntelligenceDocumentChat,
        FileMenuActionEnum.Classify,
      ].includes(action.key as FileMenuActionEnum);

      const baseItem = {
        extra: action.shortcutId ? <ShortcutHint shortcutId={action.shortcutId} /> : undefined,
        label: isAiAction ? (
          // Custom gradient for AI menu item
          <span className="animate-gradient text-ai-gradient">{getLabel(action.label)}</span>
        ) : (
          getLabel(action.label)
        ),
        disabled: action.disabled,
        icon: action.icon,
        key: action.key,
        danger: action.danger,
      };

      // Handle nested (submenu) actions recursively
      if (action.children) {
        return {
          ...baseItem,
          type: 'submenu',
          children: action.children.map(mapActionToMenuItem),
        };
      }
      return {
        ...baseItem,
        type: 'item',
      };
    };

    const items = filesActions.map(mapActionToMenuItem);
    if (items.length === 0) {
      return [
        {
          type: 'item',
          label: t('common.noActionsAvailable'),
          disabled: true,
          key: 'noActions',
        },
      ];
    }
    return items;
  }, [filesActions, t, files]);

  /**
   * Handles menu item selection.
   * Calls the main onAction handler with IDs and action key of the clicked item.
   */
  const handleMenuClick: MenuProps['onClick'] = (info) => {
    if (!files) return;

    onAction(
      files.map((file) => file.id),
      info.key as FileMenuActionEnum,
    );
  };

  // Menu width in px for positioning/collision detection
  const menuWidth = 180;
  let left = position.x;
  let top = position.y;

  // Prevent menu from overflowing screen on the right
  if (left + menuWidth > window.innerWidth) {
    left = window.innerWidth - menuWidth - 10;
  }

  // Render the Dropdown context menu
  return (
    <Dropdown
      autoAdjustOverflow
      getPopupContainer={() => document.body}
      menu={{
        // The context menu items array, including a header row and main items.
        items: [
          {
            // Disabled header row showing selected file or file count.
            label: (
              <span className="text-gray-dark block w-[170px] truncate pl-2 text-sm">
                {files.length > 1 ? (
                  <TruncatedTooltip>
                    {`${t('common.selectedItems')}: ${files.length}`}
                  </TruncatedTooltip>
                ) : (
                  <TruncatedTooltip>
                    {firstFile && removeShortcutExtension(getFileTitle(firstFile))}
                  </TruncatedTooltip>
                )}
              </span>
            ),
            disabled: true,
            key: 'file',
            icon: (
              <>
                {files.length > 1 ? (
                  <Icon name="FilesIcon" className="h-4 w-4" />
                ) : (
                  <FileIcon
                    fileId={firstFile?.id}
                    allowedFilePreviewExtensions={FILE_PREVIEW_EXTENSIONS_IMAGES}
                    isShortcut={firstFile?.isShortcut}
                    isFolder={firstFile?.isFolder}
                    isAiGrant={firstFile?.specialFolder === SpecialFolderTypeEnum.AiGrant}
                    fileExtension={removeShortcutExtension(firstFile?.fileExtension || '')}
                    secretPreset={firstFile?.secretPreset}
                    className="h-4 w-4"
                    style={{
                      color: getFolderHex(firstFile!),
                    }}
                  />
                )}
              </>
            ),
          },
          {
            type: 'divider',
          },
          ...menuItems,
        ],
        onClick: handleMenuClick,
      }}
      classNames={{
        root: 'file-context-menu',
      }}
      trigger={['contextMenu']}
      open={visible}
      styles={{
        root: {
          position: 'fixed',
          top,
          left,
          pointerEvents: 'auto',
          maxHeight: 'max-content',
          overflow: 'visible',
        },
      }}
      transitionName={position.direction === 'up' ? 'slide-up-custom' : 'slide-down-custom'}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    />
  );
};

export default FileContextMenu;
